// src/pages/Prof.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import RequireAuth from "../components/RequireAuth";
import { useAuth } from "../context/AuthContext";
import { db, storage, functions } from "../firebase";
import { useNavigate, useLocation } from "react-router-dom";

import {
  addDoc, collection, deleteDoc, doc, getDoc, getDocs, onSnapshot,
  query, serverTimestamp, updateDoc, where, setDoc, orderBy
} from "firebase/firestore";
import {
  ref as sRef,
  getDownloadURL,
  updateMetadata,
  getMetadata,
  getBytes
} from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { matiereToSlug } from "../authHelpers";

// ---------- PDF.js ----------
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Arrière-plan Espace prof
import profBg from "../assets/horloge-et-papiers-avec-fond-brillant.jpg";

// ===================== R2 / Worker (Solution 1) =====================
// Domaine du Worker (même que pour /upload, /mp/* et /v/<key>)
const VIDEOS_WORKER = "https://videos.educapsules.com";

// Lecture R2: /v/<key>
const r2UrlFromKey = (key) => `${VIDEOS_WORKER}/v/${encodeURIComponent(key)}`;

// Token Bearer (si tu l'as activé côté Worker)
let WORKER_BEARER = null;
try {
  WORKER_BEARER =
    (typeof window !== "undefined" && (window.__WORKER_BEARER || window.REACT_APP_VIDEOS_WORKER_BEARER)) ||
    (typeof process !== "undefined" && process?.env?.REACT_APP_VIDEOS_WORKER_BEARER) ||
    null;
} catch {
  WORKER_BEARER = null;
}
const authHdr = WORKER_BEARER ? { Authorization: `Bearer ${WORKER_BEARER}` } : {};

// Seuil "gros fichiers" (même ordre d'idée que côté Worker)
const LARGE_THRESHOLD = 90 * 1024 * 1024; // 90 MiB
const PART_SIZE = 8 * 1024 * 1024;        // 8 MiB par chunk

// ---------------------- Helpers HTTP ----------------------
async function readJsonOrExplain(resp) {
  const ct = resp.headers.get("content-type") || "";
  const text = await resp.text();
  if (!ct.includes("application/json")) {
    throw new Error(`Réponse non-JSON du Worker: ${text}`);
  }
  try { return JSON.parse(text); }
  catch { throw new Error(`JSON invalide: ${text}`); }
}

// Upload simple (petits fichiers) : POST /upload?key=...
async function uploadSmallViaWorker(file, key) {
  const res = await fetch(`${VIDEOS_WORKER}/upload?key=${encodeURIComponent(key)}`, {
    method: "POST",
    headers: { "Content-Type": file.type || "application/octet-stream", ...authHdr },
    body: file,
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Upload via /upload échoué (${res.status}) — ${txt.slice(0, 500)}`);
  }
  return readJsonOrExplain(res); // {ok:true, key}
}

// Upload large en chunks via Worker (multipart proxifié) : /mp/*
async function uploadLargeViaWorker(file, key, onProgress) {
  // 1) start
  const startRes = await fetch(`${VIDEOS_WORKER}/mp/start?key=${encodeURIComponent(key)}`, {
    method: "POST",
    headers: { "X-Content-Type": file.type || "application/octet-stream", ...authHdr },
  });
  if (!startRes.ok) {
    const txt = await startRes.text().catch(() => "");
    throw new Error(`mp/start failed ${startRes.status} — ${txt.slice(0, 500)}`);
  }
  const { uploadId } = await startRes.json();

  // 2) parts
  const parts = [];
  let partNumber = 1;
  let sent = 0;

  for (let off = 0; off < file.size; off += PART_SIZE, partNumber++) {
    const chunk = file.slice(off, Math.min(off + PART_SIZE, file.size));
    const partRes = await fetch(
      `${VIDEOS_WORKER}/mp/part?key=${encodeURIComponent(key)}&uploadId=${encodeURIComponent(uploadId)}&partNumber=${partNumber}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/octet-stream", ...authHdr },
        body: chunk,
      }
    );
    if (!partRes.ok) {
      const txt = await partRes.text().catch(() => "");
      throw new Error(`mp/part #${partNumber} failed ${partRes.status} — ${txt.slice(0, 500)}`);
    }
    const { etag } = await partRes.json();
    parts.push({ partNumber, etag });

    sent += chunk.size;
    onProgress?.(sent, file.size);
  }

  // 3) complete
  const compRes = await fetch(
    `${VIDEOS_WORKER}/mp/complete?key=${encodeURIComponent(key)}&uploadId=${encodeURIComponent(uploadId)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHdr },
      body: JSON.stringify({ parts }),
    }
  );
  if (!compRes.ok) {
    const txt = await compRes.text().catch(() => "");
    throw new Error(`mp/complete failed ${compRes.status} — ${txt.slice(0, 500)}`);
  }
  return readJsonOrExplain(compRes); // {ok:true}
}

async function deleteFromR2(key) {
  const res = await fetch(`${VIDEOS_WORKER}/object?key=${encodeURIComponent(key)}`, {
    method: "DELETE",
    headers: { ...authHdr },
  });
  if (!res.ok && res.status !== 404) {
    const t = await res.text().catch(() => "");
    throw new Error(`Suppression R2 échouée: ${res.status} ${t}`);
  }
}

// ===================== PDF Canvas Viewer =====================
function PdfCanvasViewer({ data, scale = 1, pageNumber = 1, onLoaded }) {
  const containerRef = useRef(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!data || !containerRef.current) return;
      containerRef.current.innerHTML = "";
      const loadingTask = pdfjsLib.getDocument({ data });
      const pdf = await loadingTask.promise;
      if (cancel) return;
      try { onLoaded && onLoaded(pdf.numPages); } catch {}
      const pn = Math.max(1, Math.min(pageNumber || 1, pdf.numPages));
      const page = await pdf.getPage(pn);
      if (cancel) return;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { alpha: false });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.display = "block";
      canvas.style.margin = "12px auto";
      // Important: refléter la taille réelle pour rendre le zoom visible
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      canvas.oncontextmenu = (e) => e.preventDefault();
      containerRef.current.appendChild(canvas);
      await page.render({ canvasContext: ctx, viewport }).promise;
    })();
    return () => { cancel = true; };
  }, [data, scale, pageNumber, onLoaded]);
  return (
    <div
      ref={containerRef}
      className="pdf-canvas-viewer"
      style={{ maxHeight: "70vh", overflow: "auto", userSelect: "none" }}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}

// ===================== Divers helpers UI =====================
const DRAG_KIND_FILE = "app/file-id";
function generateReferralCode() {
  const alphabet = "BCDFGHJKMNPQRSTVWXYZ23456789";
  const chunk = () =>
    Array.from({ length: 4 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join("");
  return `${chunk()}-${chunk()}-${chunk()}`;
}

// ===================== Page Prof =====================
function ProfInner() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);

  // Redirection si le prof est sur une autre page que /prof
  useEffect(() => {
    if (profile?.role === "prof" && !location.pathname.startsWith("/prof")) {
      navigate("/prof", { replace: true });
    }
  }, [profile, location, navigate]);

  // Onglets - suppression de l'onglet élève pour les profs
  const [activeTab, setActiveTab] = useState("content");

  // Filtrage des onglets visibles selon la matière de l'enseignant
  const subjectSlug = (matiereToSlug(profile?.subject || "") || "").toLowerCase();
  const isAdmin = (profile?.role || "") === "admin";
  const allowedTabs = useMemo(() => {
    // 🚩 Admin voit les deux onglets de contenu
    if (isAdmin) return ["content", "content3ac"];
    const both = ["svt", "pc", "math"];               // SVT, PC, Math → 2Bac et 3AC Fr
    const only2 = ["philo", "ang"];                    // Philo, Anglais → 2Bac uniquement
    const only3 = ["ei", "hg", "ar", "fr"];         // EI, HG, Arabe, Français → 3AC Fr uniquement
    if (both.includes(subjectSlug)) return ["content", "content3ac"];
    if (only2.includes(subjectSlug)) return ["content"];
    if (only3.includes(subjectSlug)) return ["content3ac"];
    // Par défaut, on expose 2Bac seulement pour éviter de montrer 3AC non voulu
    return ["content"];
  }, [subjectSlug, isAdmin]);

  // Sections visibles dans "Les inscrits" selon matière
  const allowedInsSections = useMemo(() => {
    // 🚩 Admin voit les deux sections
    if (isAdmin) return ["2bac", "3acfr"];
    const both = ["svt", "pc", "math"];               // SVT, PC, Math → 2Bac et 3AC Fr
    const only2 = ["philo", "ang"];                    // Philo, Anglais → 2Bac uniquement
    const only3 = ["ei", "hg", "ar", "fr"];         // EI, HG, Arabe, Français → 3AC Fr uniquement
    if (both.includes(subjectSlug)) return ["2bac", "3acfr"];
    if (only2.includes(subjectSlug)) return ["2bac"];
    if (only3.includes(subjectSlug)) return ["3acfr"];
    return ["2bac"];
  }, [subjectSlug, isAdmin]);

  // Filtre courant pour l'onglet Inscrits ("2bac" | "3acfr")
  const [insFilter, setInsFilter] = useState("2bac");
  useEffect(() => {
    // Ajuster le filtre par défaut quand la matière change
    if (!allowedInsSections.includes(insFilter)) {
      setInsFilter(allowedInsSections[0]);
    }
  }, [allowedInsSections]);

  // Filtre courant pour l'onglet Parrainages ("2bac" | "3acfr")
  const [refFilter, setRefFilter] = useState("2bac");
  useEffect(() => {
    if (!allowedInsSections.includes(refFilter)) {
      setRefFilter(allowedInsSections[0]);
    }
  }, [allowedInsSections]);

  // Ne recadre QUE les onglets de contenu; ne touche pas Parrainages/Inscrits
  const CONTENT_TABS = ["content", "content3ac"];
  useEffect(() => {
    if (CONTENT_TABS.includes(activeTab) && !allowedTabs.includes(activeTab)) {
      setActiveTab(allowedTabs[0]);
    }
  }, [allowedTabs, activeTab]);

  // Fonction fullscreenVideo supprimée


  // Inscrits approuvés (par mois depuis octobre)
  const [approvedSubs, setApprovedSubs] = useState([]);
  // Abonnements approuvés complets (pour filtrage dans Parrainages)
  const [approvedSubsData, setApprovedSubsData] = useState([]);
  const [insBusy, setInsBusy] = useState(false);
  const [insErr, setInsErr] = useState("");

  const academicStart = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth(); // 0..11
    const startYear = m >= 9 ? y : y - 1; // Octobre = 9
    return new Date(startYear, 9, 1, 0, 0, 0, 0);
  }, []);

  useEffect(() => {
    // Abonnements approuvés (on filtre côté client par mois/dates)
    setInsBusy(true);
    setInsErr("");
    const q = query(collection(db, "subscriptions"), where("status", "==", "approved"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = [];
        snap.forEach((d) => {
          const x = d.data() || {};
          const at = x.approvedAt && x.approvedAt.toDate ? x.approvedAt.toDate() : null;
          // id du doc = généralement UID élève
          rows.push({ id: d.id, approvedAt: at });
        });
        setApprovedSubs(rows);
        setInsBusy(false);
      },
      (e) => {
        setInsErr(e?.message || String(e));
        setInsBusy(false);
      }
    );
    return () => unsub();
  }, []);

  // Carte des élèves (users role=eleve) pour déterminer le niveau/section
  const [studentsMap, setStudentsMap] = useState(new Map());
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "users"), where("role", "==", "eleve")),
      (snap) => {
        const m = new Map();
        snap.forEach((d) => m.set(d.id, { id: d.id, ...d.data() }));
        setStudentsMap(m);
      },
      (e) => {
        console.error("users snapshot error", e);
      }
    );
    return () => unsub && unsub();
  }, []);

  // Carte des profs pour afficher le parrain
  const [profsMap, setProfsMap] = useState(new Map());
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "users"), where("role", "==", "prof")),
      (snap) => {
        const m = new Map();
        snap.forEach((d) => m.set(d.id, { id: d.id, ...d.data() }));
        setProfsMap(m);
      },
      (e) => console.error("profs snapshot error", e)
    );
    return () => unsub && unsub();
  }, []);

  // Détails du mois sélectionné (pour modal)
  const [selectedMonthDetails, setSelectedMonthDetails] = useState(null);

  // Dossiers
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [crumbs, setCrumbs] = useState([{ id: null, name: "Racine" }]);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderRestricted, setNewFolderRestricted] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  // Cible de dépôt: élèves inscrits vs contenu gratuit
  const [uploadTarget, setUploadTarget] = useState("students"); // "students" | "free"

  // Ne pas réinitialiser automatiquement le dossier courant lors des changements d’onglet
  // ou de cible d’upload. Cela évite que les nouveaux fichiers soient déposés à la racine
  // alors qu’un sous-dossier est sélectionné.
  // (Si nécessaire, l’utilisateur peut revenir à la racine avec le bouton "← Retour".)

  // Fichiers
  const [files, setFiles] = useState([]);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [pct, setPct] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const fileInputRef = useRef(null);

  // Renommage fichier
  const [fileRenamingId, setFileRenamingId] = useState(null);
  const [fileRenameValue, setFileRenameValue] = useState("");

  // Aperçu intégré
  const [previewItem, setPreviewItem] = useState(null);
  const [previewImgUrl, setPreviewImgUrl] = useState("");
  const [previewVideoUrl, setPreviewVideoUrl] = useState("");
  const [pdfBytes, setPdfBytes] = useState(null);
  const [previewKind, setPreviewKind] = useState("");
  // Contrôles PDF
  const [pdfScale, setPdfScale] = useState(1);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPages, setPdfPages] = useState(0);
  // Remise à zéro uniquement quand le document change
  useEffect(() => {
    setPdfPage(1);
    setPdfScale(1);
  }, [pdfBytes]);

  // Drag visuals
  const [isOverDrop, setIsOverDrop] = useState(false);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);

  // Parrainages
  const [withReferralCount, setWithReferralCount] = useState(0);
  const [withoutMeCount, setWithoutMeCount] = useState(0);
  const [myCodes, setMyCodes] = useState([]);
  const [genBusy, setGenBusy] = useState(false);
  const [codeUsage, setCodeUsage] = useState({});
  const [newCodeLabel, setNewCodeLabel] = useState("");
  const [codeEditingId, setCodeEditingId] = useState(null);
  // Paiements réglés (affichage complémentaire dans Parrainages)
  const [paidCountSettled, setPaidCountSettled] = useState(0);
  const [codeLabelValue, setCodeLabelValue] = useState("");

  // Tri personnalisé des fichiers et dossiers
  const [customOrder, setCustomOrder] = useState({});
  const [sortMode, setSortMode] = useState("name"); // "name", "date", "size", "custom"

  const totalFiles = filesToUpload.length;
  const previewRef = useRef(null);

  // Scroll vers la vidéo quand on clique sur un fichier
  useEffect(() => {
    if (previewItem && previewRef.current) {
      previewRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [previewItem]);

  // Charger l'ordre personnalisé
  useEffect(() => {
    if (!user) return;
    const prefId = `${user.uid}__${currentFolderId || "root"}_order`;
    const dref = doc(db, "customOrder", prefId);
    const unsub = onSnapshot(dref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setCustomOrder(data.order || {});
        setSortMode(data.sortMode || "name");
      } else {
        setCustomOrder({});
        setSortMode("name");
      }
    });
    return () => unsub();
  }, [user, currentFolderId]);

  // Sauvegarder l'ordre personnalisé
  const saveCustomOrder = async (newOrder) => {
    if (!user) return;
    const prefId = `${user.uid}__${currentFolderId || "root"}_order`;
    await setDoc(doc(db, "customOrder", prefId), {
      ownerUid: user.uid,
      folderId: currentFolderId || null,
      order: newOrder,
      sortMode: sortMode,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  // Fil d'Ariane
  useEffect(() => {
    (async () => {
      if (!currentFolderId) { setCrumbs([{ id: null, name: "Racine" }]); return; }
      const trail = [];
      let nodeId = currentFolderId;
      for (let i = 0; i < 20 && nodeId; i++) {
        const s = await getDoc(doc(db, "folders", nodeId));
        if (!s.exists()) break;
        const d = s.data();
        trail.push({ id: s.id, name: d.name, parentId: d.parentId || null });
        nodeId = d.parentId || null;
      }
      trail.reverse();
      setCrumbs([{ id: null, name: "Racine" }, ...trail]);
    })();
  }, [currentFolderId]);

  // Sous-dossiers avec tri (inclure legacy sans scope explicite)
  useEffect(() => {
    if (!user) return;
    const baseConds = [
      where("parentId", "==", currentFolderId || null)
    ];
    // Si ce n'est pas un admin ni le prof de math, on restreint à ses propres dossiers
    if (!isAdmin && subjectSlug !== "math") {
      baseConds.push(where("ownerUid", "==", user.uid));
    }

    // On interroge sans filtre de scope pour ne pas exclure les anciens dossiers sans champ scope.
    const qFolders = query(collection(db, "folders"), ...baseConds);
    const unsub = onSnapshot(qFolders, (snap) => {
      let arr = [];
      snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
      // Filtrer par onglet avec détection robuste
      if (activeTab === "content") {
        // 2Bac: exclure ce qui ressemble à 3AC
        arr = arr.filter((f) => !matches3AC(f));
      } else if (activeTab === "content3ac") {
        // 3AC: n'inclure QUE ce qui est explicitement 3AC
        arr = arr.filter((f) => matches3AC(f));
      }
      // Filtrer selon cible: élèves inscrits vs contenu gratuit
      arr = arr.filter((f) => uploadTarget === "free" ? f.free === true : f.free !== true);
      
      // Appliquer le tri
      const sorted = sortItems(arr, folders, customOrder, sortMode);
      setFolders(sorted);
    });
    return () => unsub();
  }, [user, currentFolderId, customOrder, sortMode, activeTab, uploadTarget, isAdmin, subjectSlug]);

  // Fichiers du dossier avec tri (inclure legacy sans scope explicite)
  useEffect(() => {
    if (!user) return;
    const baseConds = [
      where("folderId", "==", currentFolderId || null)
    ];
    // Si ce n'est pas un admin ni le prof de math, on restreint à ses propres fichiers
    if (!isAdmin && subjectSlug !== "math") {
      baseConds.push(where("ownerUid", "==", user.uid));
    }
    
    // On interroge sans filtre de scope pour ne pas exclure les anciens fichiers sans champ scope.
    const qFiles = query(collection(db, "contents"), ...baseConds);
    const unsub = onSnapshot(qFiles, (snap) => {
      let arr = [];
      snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
      // Filtrer par onglet avec détection robuste
      if (activeTab === "content") {
        arr = arr.filter((f) => !matches3AC(f));
      } else if (activeTab === "content3ac") {
        // 3AC: n'inclure QUE ce qui est explicitement 3AC
        arr = arr.filter((f) => matches3AC(f));
      }
      // Filtrer par sous-onglet (élèves inscrits vs contenu gratuit)
      arr = arr.filter((f) => uploadTarget === "free" ? f.free === true : f.free !== true);
      
      // Appliquer le tri
      const sorted = sortItems(arr, files, customOrder, sortMode);
      setFiles(sorted);
      setPreviewItem((p) => (p && !arr.find(x => x.id === p.id) ? null : p));
    });
    return () => unsub();
  }, [user, currentFolderId, customOrder, sortMode, activeTab, uploadTarget, isAdmin, subjectSlug]);

  // Fonction de tri générique
  function sortItems(newItems, oldItems, order, mode) {
    const items = [...newItems];
    
    switch (mode) {
      case "custom":
        // Tri par ordre personnalisé
        items.sort((a, b) => {
          const orderA = order[a.id] || Number.MAX_SAFE_INTEGER;
          const orderB = order[b.id] || Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
        break;
        
      case "date":
        items.sort((a, b) => {
          const dateA = a?.createdAt?.seconds || 0;
          const dateB = b?.createdAt?.seconds || 0;
          return dateB - dateA; // Plus récent d'abord
        });
        break;
        
      case "size":
        items.sort((a, b) => {
          const sizeA = a.sizeBytes || 0;
          const sizeB = b.sizeBytes || 0;
          return sizeB - sizeA; // Plus gros d'abord
        });
        break;
        
      case "name":
      default:
        items.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
    }
    
    return items;
  }

  // Détection robuste des sections 3AC / 2Bac
  const normalize = (v) => (v || "").toString().toLowerCase();
  const matches3AC = (x) => {
    const s = [normalize(x.scope), normalize(x.section), normalize(x.level), normalize(x.folderPath), normalize(x.name)]
      .join(" ");
    return s.includes("3ac");
  };
  const matches2Bac = (x) => {
    const s = [normalize(x.scope), normalize(x.section), normalize(x.level), normalize(x.folderPath), normalize(x.name)]
      .join(" ");
    return s.includes("2bac");
  };

  // Codes du prof
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "refCodes"), where("profId", "==", user.uid));
    const unsub = onSnapshot(q, (snap) => {
      const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      arr.sort((a,b) => (b?.createdAt?.seconds || 0) - (a?.createdAt?.seconds || 0));
      setMyCodes(arr);
    });
    return () => unsub();
  }, [user]);

  // Inscriptions approuvées (compteurs)
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "subscriptions"), where("status", "==", "approved"));
    const unsub = onSnapshot(q, (snap) => {
      let total = 0, withMe = 0;
      const perCode = {};
      const arr = [];
      snap.forEach((d) => {
        total++;
        const x = d.data();
        // Conserver données détaillées pour filtrage par section
        arr.push({ id: d.id, ...x, approvedAt: x?.approvedAt?.toDate ? x.approvedAt.toDate() : null });
        if (x?.referrerProfId === user.uid) {
          withMe++;
          const code = (x.referralCode || "").toUpperCase();
          if (code) perCode[code] = (perCode[code] || 0) + 1;
        }
      });
      setApprovedSubsData(arr);
      setWithReferralCount(withMe);
      setWithoutMeCount(Math.max(0, total - withMe));
      setCodeUsage(perCode);
    });
    return () => unsub();
  }, [user]);

  // Paiements réglés pour ce prof (par section Parrainages)
  useEffect(() => {
    if (!user) return;
    const key = `${user.uid}__${refFilter}`;
    const d = doc(db, "profPayments", key);
    const unsub = onSnapshot(d, (snap) => {
      const val = Number(snap.data()?.paidCount || 0);
      setPaidCountSettled(val);
    });
    return () => unsub();
  }, [user, refFilter]);

  // Actions dossiers
  async function createFolder() {
    const name = (newFolderName || "").trim(); if (!name || !user) return;
    await addDoc(collection(db, "folders"), {
      ownerUid: user.uid,
      name,
      parentId: currentFolderId || null,
      createdAt: serverTimestamp(),
      restricted: !!newFolderRestricted,
      restrictedMsg: "Pas pour le moment",
      scope: activeTab === "content3ac" ? "3AC-Fr" : null,
      free: uploadTarget === "free",
    });
    setNewFolderName("");
    setNewFolderRestricted(false);
  }

  async function renameFolder(f) {
    const val = (renameValue || "").trim(); if (!val) return;
    await updateDoc(doc(db, "folders", f.id), { name: val }); setRenamingId(null); setRenameValue("");
  }

  async function toggleFolderRestricted(f) {
    await updateDoc(doc(db, "folders", f.id), {
      restricted: !f.restricted,
      updatedAt: serverTimestamp(),
    });
  }

  async function deleteFolder(f) {
    const q1 = query(collection(db,"folders"), where("ownerUid","==",user.uid), where("parentId","==",f.id));
    const q2 = query(collection(db,"contents"), where("ownerUid","==",user.uid), where("folderId","==",f.id));
    const s1 = await getDocs(q1), s2 = await getDocs(q2);
    if (!s1.empty || !s2.empty) return alert("Le dossier n'est pas vide.");
    await deleteDoc(doc(db,"folders",f.id));
  }

  async function goUp() {
    if (!currentFolderId) return;
    const s = await getDoc(doc(db,"folders",currentFolderId));
    setCurrentFolderId(s.exists()? (s.data().parentId || null) : null);
  }

  // Gestion de l'ordre personnalisé
  const moveItem = async (itemId, direction) => {
    const newOrder = { ...customOrder };
    const items = [...folders, ...files];
    const currentIndex = Object.keys(newOrder).indexOf(itemId);
    
    if (direction === 'up' && currentIndex > 0) {
      const keys = Object.keys(newOrder);
      [newOrder[keys[currentIndex]], newOrder[keys[currentIndex - 1]]] = 
      [newOrder[keys[currentIndex - 1]], newOrder[keys[currentIndex]]];
    } else if (direction === 'down' && currentIndex < Object.keys(newOrder).length - 1) {
      const keys = Object.keys(newOrder);
      [newOrder[keys[currentIndex]], newOrder[keys[currentIndex + 1]]] = 
      [newOrder[keys[currentIndex + 1]], newOrder[keys[currentIndex]]];
    }
    
    await saveCustomOrder(newOrder);
  };

  const setItemOrder = async (itemId, position) => {
    const newOrder = {};
    const items = [...folders, ...files];
    
    items.forEach((item, index) => {
      if (item.id === itemId) {
        newOrder[itemId] = position;
      } else if (customOrder[item.id] !== undefined) {
        newOrder[item.id] = customOrder[item.id] >= position ? customOrder[item.id] + 1 : customOrder[item.id];
      } else {
        newOrder[item.id] = index >= position ? index + 1 : index;
      }
    });
    
    await saveCustomOrder(newOrder);
  };

  // ----------------------- UPLOAD -----------------------
  async function doUpload() {
    if (!filesToUpload.length || !user) return;
    setUploadErr("");
    setUploading(true);
    setPct(0);

    // Préférer profile.subject, puis profile.matiere comme repli pour assurer le bon filtrage côté élève
    const subject = profile?.subject || profile?.matiere || "unknown";
    const folderId = currentFolderId || "root";

    const addFirestoreDoc = async (name, key, contentType, sizeBytes) => {
      const levelSection = activeTab === "content3ac" ? "3AC-Fr" : "2Bac";
      const isFree = uploadTarget === "free";
      await addDoc(collection(db, "contents"), {
        ownerUid: user.uid,
        name,
        r2Key: key,
        // Utiliser le folderId résolu; root -> null pour compatibilité avec Eleve.jsx
        folderId: folderId === "root" ? null : folderId,
        createdAt: serverTimestamp(),
        subject,
        matiere: subject,
        visibility: "public",
        contentType,
        sizeBytes: typeof sizeBytes === "number" ? sizeBytes : null,
        // Filtrage par niveau/matière côté Gratuit.jsx
        section: levelSection,
        scope: levelSection,
        // Marqueur de contenu gratuit
        free: isFree,
        // Sujet explicite pour l’onglet "Contenu gratuit"
        freeSubject: isFree ? subject : null,
      });
    };

    const totalBytes = filesToUpload.reduce((s, f) => s + (f.size || 0), 0);
    let uploadedBytes = 0;
    const updateProgress = (delta) => {
      uploadedBytes += delta;
      const pc = totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;
      setPct(pc);
    };

    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        const f = filesToUpload[i];
        const key = `${folderId}/${Date.now()}_${f.name}`;

        try {
          if (f.size > LARGE_THRESHOLD) {
            await uploadLargeViaWorker(f, key, (sentForFile, totalForFile) => {
              const baseDone = uploadedBytes - (i > 0 ? filesToUpload.slice(0, i).reduce((s, ff) => s + ff.size, 0) : 0);
              const learned = Math.max(0, sentForFile - baseDone);
              updateProgress(learned);
            });
            updateProgress(f.size - (uploadedBytes - (i > 0 ? filesToUpload.slice(0, i).reduce((s, ff) => s + ff.size, 0) : 0)));
          } else {
            await uploadSmallViaWorker(f, key);
            updateProgress(f.size || 0);
          }
        } catch (err) {
          const msg = String(err?.message || err);
          const looks413 = /413|Content Too Large/i.test(msg);
          if (!looks413) throw err;

          await uploadLargeViaWorker(f, key, (sentForFile, totalForFile) => {
            const baseDone = uploadedBytes - (i > 0 ? filesToUpload.slice(0, i).reduce((s, ff) => s + ff.size, 0) : 0);
            const learned = Math.max(0, sentForFile - baseDone);
            updateProgress(learned);
          });
          updateProgress(f.size - (uploadedBytes - (i > 0 ? filesToUpload.slice(0, i).reduce((s, ff) => s + ff.size, 0) : 0)));
        }

        await addFirestoreDoc(f.name, key, f.type || "application/octet-stream", f.size);
      }
    } catch (e) {
      console.error(e);
      setUploadErr(e.message || "Erreur upload");
    } finally {
      setUploading(false);
      setFilesToUpload([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setTimeout(() => setPct(0), 800);
    }
  }

  // Suppression
  async function deleteFile(item) {
    if (!window.confirm("Supprimer ce fichier ?")) return;
    try {
      if (item.r2Key) {
        await deleteFromR2(item.r2Key);
      } else if (item.storagePath) {
        try {
          const purge = httpsCallable(functions, "purgeLegacy");
          await purge({ storagePath: item.storagePath });
        } catch (e) {
          console.warn("purgeLegacy fallback:", e?.message || e);
        }
      }
      await deleteDoc(doc(db, "contents", item.id));
      setPreviewItem((p) => (p && p.id === item.id ? null : p));
      if (previewImgUrl) URL.revokeObjectURL(previewImgUrl);
      setPreviewImgUrl("");
      setPreviewVideoUrl("");
      setPdfBytes(null);
      setPreviewKind("");
    } catch (e) {
      console.error(e);
      alert("Suppression impossible : " + (e.code || e.message));
    }
  }

  // Métadonnées Storage pour inline
  async function ensureInlineMeta(item) {
    try {
      if (!item?.storagePath) return;
      const r = sRef(storage, item.storagePath);
      const meta = await getMetadata(r);
      const ct = (meta.contentType || "").toLowerCase();
      const cd = (meta.contentDisposition || "").toLowerCase();
      const ext = (item.name || "").toLowerCase().split(".").pop();
      const isPdf   = ext === "pdf";
      const isImage = ["png","jpg","jpeg","gif","webp","bmp","svg"].includes(ext);
      const isVideo = ["mp4","webm","ogg"].includes(ext);
      const wantCT = isPdf ? "application/pdf" : (isImage ? (ct || "image/*") : (isVideo ? (ct || "video/*") : ct));
      const needFix = !cd.startsWith("inline") || (isPdf && !ct.startsWith("application/pdf"));
      if (needFix) {
        await updateMetadata(r, {
          contentType: wantCT || meta.contentType,
          contentDisposition: `inline; filename="${(item.name || "file").replace(/"/g, "")}"`,
        });
      }
    } catch (e) {
      console.warn("ensureInlineMeta:", e);
    }
  }

  // Ouvrir
  async function onOpen(item) {
    if (!item) return;
    const ext = (item.name || "").toLowerCase().split(".").pop();
    setPreviewItem(item);
    setPreviewImgUrl(""); setPreviewVideoUrl(""); setPdfBytes(null); setPreviewKind("");

    if (item.r2Key) {
      const url = r2UrlFromKey(item.r2Key);
      try {
        if (["png","jpg","jpeg","gif","webp","bmp","svg"].includes(ext)) {
          setPreviewKind("img"); setPreviewImgUrl(url); return;
        }
        if (ext === "pdf") {
          try {
            const resp = await fetch(url, { credentials: "omit" });
            const buf = await resp.arrayBuffer();
            setPreviewKind("pdf"); setPdfBytes(buf); return;
          } catch {
            setPreviewKind("doc");
            setPdfBytes(null);
            setPreviewVideoUrl(`https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(url)}`);
            return;
          }
        }
        if (["mp4","webm","ogg","mov","m4v"].includes(ext)) {
          setPreviewKind("video"); setPreviewVideoUrl(url); return;
        }
        setPreviewKind("doc");
        setPreviewVideoUrl(`https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(url)}`);
        return;
      } catch (e) {
        console.warn("open preview (R2) failed:", e);
      }
    }

    // Fallback ancien Storage
    await ensureInlineMeta(item);
    try {
      if (item.storagePath) {
        const r = sRef(storage, item.storagePath);
        if (["png","jpg","jpeg","gif","webp","bmp","svg"].includes(ext)) {
          const bytes = await getBytes(r);
          const meta = await getMetadata(r);
          const blob = new Blob([bytes], { type: meta.contentType || "image/*" });
          const local = URL.createObjectURL(blob);
          setPreviewKind("img"); setPreviewImgUrl(local); return;
        }
        if (ext === "pdf") {
          const bytes = await getBytes(r);
          setPreviewKind("pdf"); setPdfBytes(bytes); return;
        }
        if (["mp4","webm","ogg"].includes(ext)) {
          const url = await getDownloadURL(r);
          setPreviewKind("video"); setPreviewVideoUrl(url); return;
        }
      }
    } catch (e) {
      console.warn("open preview (Storage) failed:", e);
    }

    const url = item.url;
    if (["png","jpg","jpeg","gif","webp","bmp","svg"].includes(ext)) { setPreviewKind("img"); setPreviewImgUrl(url); return; }
    if (ext === "pdf") {
      try { const resp = await fetch(url, { credentials: "include" }); const buf = await resp.arrayBuffer(); setPreviewKind("pdf"); setPdfBytes(buf); }
      catch {}
      return;
    }
    if (["mp4","webm","ogg"].includes(ext)) { setPreviewKind("video"); setPreviewVideoUrl(url); return; }
    setPreviewKind("doc");
    setPdfBytes(null);
    setPreviewVideoUrl(`https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(url)}`);
  }

  // DnD déplacer fichier
  function onFileDragStart(e, item) {
    e.dataTransfer.setData(DRAG_KIND_FILE, JSON.stringify({ id: item.id }));
    e.dataTransfer.effectAllowed = "move";
  }
  function onFolderDragOver(e, folderId) {
    if (e.dataTransfer.types.includes(DRAG_KIND_FILE)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverFolderId(folderId ?? "__root__");
    }
  }
  async function onFolderDrop(e, folderId) {
    const raw = e.dataTransfer.getData(DRAG_KIND_FILE);
    setDragOverFolderId(null);
    if (!raw) return;
    const { id } = JSON.parse(raw);
    await updateDoc(doc(db,"contents",id), { folderId: folderId || null, movedAt: serverTimestamp() });
  }

  // DnD upload
  function onFilesDragOver(e) { e.preventDefault(); setIsOverDrop(true); }
  function onFilesDragEnter(e){ e.preventDefault(); setIsOverDrop(true); }
  function onFilesDragLeave(){ setIsOverDrop(false); }
  function onFilesDrop(e){
    e.preventDefault(); setIsOverDrop(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    if (dropped.length > 0) setFilesToUpload(dropped);
  }

  // Générer un code parrainage (avec mémo optionnel)
  async function handleGenerateCode() {
    if (!user) return;
    try {
      setGenBusy(true);
      const code = generateReferralCode();
      const label = (newCodeLabel || "").toString().slice(0, 60).trim() || null;
      await setDoc(doc(db, "refCodes", code), {
        active: true,
        profId: user.uid,
        discount: 50,
        subject: profile?.subject || null,
        createdAt: serverTimestamp(),
        label,
      });
      setNewCodeLabel("");
      alert(`Code créé : ${label ? label + " — " : ""}${code} (réduction 50 DH)`);
    } catch (e) {
      console.error(e);
      alert("Échec de la création du code.");
    } finally {
      setGenBusy(false);
    }
  }

  async function handleSaveCodeLabel(id) {
    const val = (codeLabelValue || "").toString().slice(0, 60).trim();
    await updateDoc(doc(db, "refCodes", id), {
      label: val || null,
      updatedAt: serverTimestamp(),
    });
    setCodeEditingId(null);
    setCodeLabelValue("");
  }

  const selectedLabel = useMemo(() => {
    if (!filesToUpload.length) return "Aucun fichier n'a été sélectionné";
    if (filesToUpload.length === 1) return filesToUpload[0].name;
    if (filesToUpload.length <= 3) return filesToUpload.map(f=>f.name).join(", ");
    return `${filesToUpload.length} fichiers sélectionnés`;
  }, [filesToUpload]);

  function PreviewPane() {
    if (!previewItem) return (
      <div className="border rounded p-3 text-muted" style={{minHeight:160}}>
        Sélectionnez un fichier pour le lire ici.
      </div>
    );
    if (previewKind === "img") {
      return (
        <div style={{position:"relative"}}>
          <img
            src={previewImgUrl}
            alt={previewItem.name}
            className="img-fluid rounded"
            style={{ maxHeight:"70vh", userSelect:"none", WebkitUserDrag:"none" }}
            draggable={false}
            onContextMenu={(e)=>e.preventDefault()}
          />
          <div style={{position:"absolute", inset:0}} onContextMenu={(e)=>e.preventDefault()} />
        </div>
      );
    }
    if (previewKind === "video") {
      return (
        <div>
          <video
            src={previewVideoUrl}
            controls
            controlsList="nodownload noplaybackrate"
            ref={videoRef}
            style={{ width:"100%", maxHeight:"70vh", borderRadius:"0.5rem" }}
            onContextMenu={(e)=>e.preventDefault()}
            disablePictureInPicture
          />
        </div>
      );
    }
    if (previewKind === "pdf") {
      return (
        <div>
          {/* Barre de contrôles PDF */}
          <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
            <div className="btn-group" role="group" aria-label="Zoom">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setPdfScale(s => Math.max(0.6, Math.round((s - 0.1) * 10) / 10))}
                disabled={!pdfBytes}
              >
                -
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setPdfScale(s => Math.min(2.5, Math.round((s + 0.1) * 10) / 10))}
                disabled={!pdfBytes}
              >
                +
              </button>
            </div>
            <select
              className="form-select form-select-sm"
              style={{ width: 100 }}
              value={String(pdfScale)}
              onChange={(e) => setPdfScale(parseFloat(e.target.value) || 1)}
              disabled={!pdfBytes}
              aria-label="Sélecteur de zoom"
            >
              <option value="0.8">80%</option>
              <option value="1">100%</option>
              <option value="1.2">120%</option>
              <option value="1.5">150%</option>
              <option value="2">200%</option>
            </select>
            <span className="small text-muted">Zoom: x{pdfScale.toFixed(1)}</span>

            <div className="input-group input-group-sm" style={{ width: 220 }}>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setPdfPage(p => Math.max(1, p - 1))}
                disabled={pdfPage <= 1 || pdfPages <= 1}
              >
                Préc.
              </button>
              <input
                type="number"
                className="form-control"
                min={1}
                max={Math.max(1, pdfPages)}
                value={pdfPage}
                onChange={(e) => {
                  const v = parseInt(e.target.value || "1", 10);
                  setPdfPage(Number.isNaN(v) ? 1 : Math.max(1, Math.min(v, Math.max(1, pdfPages))));
                }}
                aria-label="Numéro de page"
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setPdfPage(p => Math.min(pdfPages, p + 1))}
                disabled={pdfPage >= pdfPages || pdfPages <= 1}
              >
                Suiv.
              </button>
            </div>
            <span className="small text-muted">Page {pdfPage} / {pdfPages || "?"}</span>
          </div>

          {/* Rendu PDF */}
          <PdfCanvasViewer
            data={pdfBytes}
            scale={pdfScale}
            pageNumber={pdfPage}
            onLoaded={(n) => { setPdfPages(n || 0); }}
          />
        </div>
      );
    }
    return (
      <iframe
        title={previewItem?.name || "document"}
        src={previewVideoUrl}
        style={{ width: "100%", height: "70vh", border: 0, borderRadius: "0.5rem" }}
        sandbox="allow-scripts allow-same-origin"
        referrerPolicy="no-referrer"
        onContextMenu={(e)=>e.preventDefault()}
      />
    );
  }

  return (
    <>
      {/* Arrière-plan responsive (ne touche pas au contenu) */}
      <div
        className="prof-bg"
        aria-hidden="true"
        style={{ "--prof-bg": `url(${profBg})` }}
      />
      
      <div
        className="container py-4"
        tabIndex={0}
        onKeyDownCapture={(e)=>{ const k=(e.key||"").toLowerCase(); const ctrl=e.ctrlKey||e.metaKey; const shift=e.shiftKey; if(k==="f12"||(ctrl&&(k==="s"||k==="p"||k==="u"))||(ctrl&&shift&&(k==="i"||k==="j"||k==="c"))){ e.preventDefault(); e.stopPropagation(); } }}
      >
        <h1 className="h4 mb-3">Espace enseignant{profile?.subject ? " · " + profile.subject : ""}</h1>

        {/* Onglets - contenu, parrainages, inscrits */}
        <ul className="nav nav-tabs mb-3">
          {allowedTabs.includes("content") && (
            <li className="nav-item">
              <button className={"nav-link " + (activeTab === "content" ? "active" : "")} onClick={() => setActiveTab("content")}>
                Contenu pédagogique du 2Bac
              </button>
            </li>
          )}
          {allowedTabs.includes("content3ac") && (
            <li className="nav-item">
              <button className={"nav-link " + (activeTab === "content3ac" ? "active" : "")} onClick={() => setActiveTab("content3ac")}>
                Contenu pédagogique du 3AC Fr
              </button>
            </li>
          )}
          <li className="nav-item">
            <button className={"nav-link " + (activeTab === "referrals" ? "active" : "")} onClick={() => setActiveTab("referrals")}>
              Parrainages
            </button>
          </li>
          <li className="nav-item">
            <button className={"nav-link " + (activeTab === "inscrits" ? "active" : "")} onClick={() => setActiveTab("inscrits")}>
              Les inscrits
            </button>
          </li>
        </ul>

        {/* Vue Inscrits approuvés par mois (depuis Octobre) */}
        {activeTab === "inscrits" && (
          (() => {
            // Génère les mois entre academicStart et aujourd'hui
            const now = new Date();
            const months = [];
            const cur = new Date(academicStart);
            while (cur <= now) {
              months.push(new Date(cur));
              cur.setMonth(cur.getMonth() + 1);
            }
            const MONTHS_FR = [
              "Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"
            ];
            const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
            const readable = (d) => `${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;

            // Filtrer par section selon insFilter
            const isInSection = (uid) => {
              const u = studentsMap.get(uid);
              const lvl = String(u?.level || "2bac").toLowerCase();
              if (insFilter === "2bac") return lvl === "2bac";
              if (insFilter === "3acfr") {
                // Accepte les niveaux explicitement 3AC, sinon tout autre que 2bac
                return lvl === "3acfr" || lvl === "3ac-fr" || lvl === "3ac" || lvl !== "2bac";
              }
              return true;
            };
            const filteredApproved = approvedSubs.filter(s => isInSection(s.id));

            const map = {};
            for (const m of months) map[monthKey(m)] = { label: readable(m), count: 0 };
            for (const s of filteredApproved) {
              if (!s.approvedAt || s.approvedAt < academicStart) continue;
              const k = monthKey(s.approvedAt);
              if (!map[k]) map[k] = { label: readable(s.approvedAt), count: 0 };
              map[k].count += 1;
            }
            const rows = Object.keys(map).sort().map(k => ({ key:k, ...map[k] }));

            // Prépare les données du graphique (SVG light, responsive)
            const maxCount = rows.length ? Math.max(...rows.map(r => r.count)) : 0;
            const total = filteredApproved.length;
            const W = 720, H = 220;
            const M = { top: 16, right: 12, bottom: 28, left: 42 };
            const CW = W - M.left - M.right;
            const CH = H - M.top - M.bottom;
            const n = rows.length;
            const xPos = (i) => M.left + (n <= 1 ? CW/2 : (i * (CW / (n - 1))));
            const yPos = (v) => M.top + (maxCount > 0 ? (CH - (v * CH / maxCount)) : CH);
            const linePath = rows.map((r, i) => (i === 0 ? `M ${xPos(i)} ${yPos(r.count)}` : `L ${xPos(i)} ${yPos(r.count)}`)).join(" ");
            const areaPath = n > 0
              ? `${rows.map((r,i)=> (i===0?`M ${xPos(i)} ${yPos(r.count)}`:`L ${xPos(i)} ${yPos(r.count)}`)).join(" ")}
                 L ${xPos(n-1)} ${M.top + CH} L ${xPos(0)} ${M.top + CH} Z`
              : "";
            const gridLines = Array.from({ length: 4 }, (_, i) => {
              const val = Math.round((maxCount * (i + 1)) / 4);
              const y = yPos(val);
              return { y, val };
            });
            const shortLabel = (label) => {
              const [m, y] = String(label).split(" ");
              const shortM = (m || "").slice(0, 3);
              return `${shortM} ${y || ""}`;
            };

            return (
              <div className="mt-2">
                <h5 className="mb-2">Les inscrits approuvés (depuis Octobre)</h5>
                {/* Contrôle de filtre par section */}
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap" style={{maxWidth: 520}}>
                  <div className="input-group">
                    <span className="input-group-text">Section</span>
                    <div className="btn-group" role="group" aria-label="Filtrer par section">
                      {allowedInsSections.includes("2bac") && (
                        <button
                          type="button"
                          className={"btn " + (insFilter === "2bac" ? "btn-primary" : "btn-outline-primary")}
                          onClick={() => setInsFilter("2bac")}
                        >
                          2Bac
                        </button>
                      )}
                      {allowedInsSections.includes("3acfr") && (
                        <button
                          type="button"
                          className={"btn " + (insFilter === "3acfr" ? "btn-primary" : "btn-outline-primary")}
                          onClick={() => setInsFilter("3acfr")}
                        >
                          3AC Fr
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {insErr && <div className="alert alert-danger">{insErr}</div>}
                {insBusy ? (
                  <div className="text-muted">Chargement…</div>
                ) : (
                  <>
                    {/* Graphique d’évolution mensuelle (SVG léger, sans dépendance) */}
                    <figure className="card shadow-sm transparent-card mb-3" style={{overflow: "hidden"}}>
                      <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                          <h6 className="mb-0">Évolution mensuelle des inscrits approuvés</h6>
                          <div className="small text-muted">Max: {maxCount} · Total: {total}</div>
                        </div>
                        <svg
                          width="100%"
                          height={H}
                          viewBox={`0 0 ${W} ${H}`}
                          role="img"
                          aria-label="Graphique des inscrits approuvés par mois"
                        >
                          {/* Grille horizontale et graduations */}
                          {gridLines.map((g, i) => (
                            <g key={`gl-${i}`}> 
                              <line x1={M.left} x2={M.left+CW} y1={g.y} y2={g.y} stroke="#000" opacity={0.06} />
                              <text x={8} y={g.y+4} fontSize={11} fill="#6c757d">{g.val}</text>
                            </g>
                          ))}
                          {/* Axe X */}
                          <line x1={M.left} x2={M.left+CW} y1={M.top+CH} y2={M.top+CH} stroke="#000" opacity={0.2} />
                          {/* Labels des mois */}
                          {rows.map((r, i) => (
                            <text key={`xl-${r.key}`} x={xPos(i)} y={M.top+CH+18} fontSize={11} textAnchor="middle" fill="#6c757d">
                              {shortLabel(r.label)}
                            </text>
                          ))}
                          {/* Zone sous la ligne */}
                          {n > 0 && (
                            <path d={areaPath}
                              fill="#0d6efd"
                              opacity={0.12}
                            />
                          )}
                          {/* Courbe principale */}
                          {n > 0 && (
                            <path d={linePath} fill="none" stroke="#0d6efd" strokeWidth={2} />
                          )}
                          {/* Points */}
                          {rows.map((r, i) => (
                            <g key={`pt-${r.key}`}> 
                              <circle cx={xPos(i)} cy={yPos(r.count)} r={3.5} fill="#0d6efd" />
                              {/* Valeurs au-dessus des points (limitées pour lisibilité) */}
                              {n <= 10 && (
                                <text x={xPos(i)} y={yPos(r.count)-8} fontSize={11} textAnchor="middle" fill="#0d6efd">{r.count}</text>
                              )}
                            </g>
                          ))}
                        </svg>
                        <figcaption className="small text-muted mt-1">Filtré par section · depuis Octobre</figcaption>
                      </div>
                    </figure>

                    {/* Tableau détaillé */}
                    <div className="table-responsive">
                      <table className="table align-middle table-hover">
                        <thead>
                          <tr>
                            <th style={{width:240}}>Mois</th>
                            <th>Nombre des inscrits approuvés</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(r => (
                            <tr 
                              key={r.key} 
                              onClick={() => setSelectedMonthDetails(r)}
                              style={{ cursor: "pointer" }}
                              title="Cliquez pour voir les détails"
                            >
                              <td>{r.label}</td>
                              <td><span className="badge text-bg-primary">{r.count}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Modal Détails Mois */}
                    {selectedMonthDetails && (() => {
                      // Mapping ID Prof -> Matière (demandé explicitement)
                      const PROF_SUBJECTS = {
                        "ZEZUF58B0JRh2t4IKqPFqGqwlvt1": "Math",
                        "A6Z10MF72sRymx1kqhxKNak9p603": "SVT",
                        "gaeWTJxb6sYSaZOkioE73Bg3UYZ2": "PC",
                        "t3bLesLL78gDvNwWUlKh2G9gTln2": "Anglais",
                        "fXzXkzOxdKc5OQ7XIvZlQRdp58O2": "Philo"
                      };

                      // Utiliser approvedSubsData (qui contient referrerProfId) au lieu de filteredApproved
                      const relevantSubs = approvedSubsData.filter(s => isInSection(s.id));

                      // Filtrer les abonnements pour ce mois
                      const monthSubs = relevantSubs.filter(s => {
                        if (!s.approvedAt) return false;
                        const k = monthKey(s.approvedAt);
                        return k === selectedMonthDetails.key;
                      });

                      return (
                        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
                          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content">
                              <div className="modal-header">
                                <h5 className="modal-title">Inscrits - {selectedMonthDetails.label}</h5>
                                <button type="button" className="btn-close" onClick={() => setSelectedMonthDetails(null)}></button>
                              </div>
                              <div className="modal-body">
                                {monthSubs.length === 0 ? (
                                  <p className="text-muted">Aucun inscrit trouvé pour ce mois.</p>
                                ) : (
                                  <div className="table-responsive">
                                    <table className="table table-sm table-striped align-middle">
                                      <thead>
                                        <tr>
                                          <th>Email Étudiant</th>
                                          <th>Prof Parrain (Matière)</th>
                                          <th>Date</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {monthSubs.map(sub => {
                                          const student = studentsMap.get(sub.id);
                                          const profId = sub.referrerProfId;
                                          // Priorité : Mapping manuel > Champ subject du prof > Nom du prof
                                          let profDisplay = null;
                                          if (profId) {
                                            if (PROF_SUBJECTS[profId]) {
                                              profDisplay = PROF_SUBJECTS[profId];
                                            } else {
                                              const pObj = profsMap.get(profId);
                                              profDisplay = pObj?.subject || pObj?.matiere || pObj?.displayName || pObj?.email || "Autre";
                                            }
                                          }

                                          return (
                                            <tr key={sub.id}>
                                              <td>
                                                <div>{student?.email || "Email introuvable"}</div>
                                                <div className="small text-muted">{student?.displayName}</div>
                                              </td>
                                              <td>
                                                {profDisplay ? (
                                                  <span className="badge text-bg-success">{profDisplay}</span>
                                                ) : (
                                                  <span className="text-muted">Aucun</span>
                                                )}
                                              </td>
                                              <td>{sub.approvedAt?.toLocaleDateString()}</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                              <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setSelectedMonthDetails(null)}>Fermer</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            );
          })()
        )}

        {/* Vue Parrainages */}
        {activeTab === "referrals" && (() => {
          // Filtrer par section
          const isInSection = (uid) => {
            const u = studentsMap.get(uid);
            const lvl = String(u?.level || "2bac").toLowerCase();
            if (refFilter === "2bac") return lvl === "2bac";
            if (refFilter === "3acfr") {
              return lvl !== "2bac" || ["3acfr","3ac-fr","3ac"].includes(lvl);
            }
            return true;
          };
          const filtered = approvedSubsData.filter(s => isInSection(s.id));
          const withMeF = filtered.filter(s => s?.referrerProfId === user?.uid).length;
          const withoutMeF = Math.max(0, filtered.length - withMeF);
          const codeUsageFiltered = {};
          for (const s of filtered) {
            if (s?.referrerProfId === user?.uid) {
              const code = (s?.referralCode || "").toUpperCase();
              if (code) codeUsageFiltered[code] = (codeUsageFiltered[code] || 0) + 1;
            }
          }

          return (
          <>
            {/* Contrôle de filtre par section */}
            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap" style={{maxWidth: 520}}>
              <div className="input-group">
                <span className="input-group-text">Section</span>
                <div className="btn-group" role="group" aria-label="Filtrer par section">
                  {allowedInsSections.includes("2bac") && (
                    <button
                      type="button"
                      className={"btn " + (refFilter === "2bac" ? "btn-primary" : "btn-outline-primary")}
                      onClick={() => setRefFilter("2bac")}
                    >
                      2Bac
                    </button>
                  )}
                  {allowedInsSections.includes("3acfr") && (
                    <button
                      type="button"
                      className={"btn " + (refFilter === "3acfr" ? "btn-primary" : "btn-outline-primary")}
                      onClick={() => setRefFilter("3acfr")}
                    >
                      3AC Fr
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="row g-4 mb-3">
              <div className="col-lg-6">
                <div className="card shadow-sm transparent-card">
                  <div className="card-body text-center">
                    <div className="text-muted">Avec parrainage (vos codes)</div>
                    <div className="display-5 fw-bold">{withMeF}</div>
                    <div className="small text-muted">Filtré par section · status = approved</div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="card shadow-sm transparent-card">
                  <div className="card-body text-center">
                    <div className="text-muted">Sans vous (autres profs ou sans code)</div>
                    <div className="display-5 fw-bold">{withoutMeF}</div>
                    <div className="small text-muted">Total filtré − avec vos codes</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Affichage complémentaire : inscriptions réglées */}
            <div className="mb-3 text-center">
              <span className="badge text-bg-success">{paidCountSettled} inscription{paidCountSettled>1?"s":""} réglée{paidCountSettled>1?"s":""}</span>
            </div>

            <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
              <h5 className="mb-0">Vos codes</h5>
              <div className="input-group" style={{maxWidth: 520}}>
                <span className="input-group-text">Mémo (optionnel)</span>
                <input
                  className="form-control"
                  placeholder="ex: Lycée Al Qods / Fès"
                  value={newCodeLabel}
                  onChange={(e)=>setNewCodeLabel(e.target.value)}
                  maxLength={60}
                />
                <button className="btn btn-primary" onClick={handleGenerateCode} disabled={genBusy}>
                  {genBusy ? "Génération…" : "Générer un code (–50 DH)"}
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Mémo</th>
                    <th>Code</th>
                    <th>Actif</th>
                    <th>Réduction</th>
                    <th>Matière</th>
                    <th>Créé le</th>
                    <th>Inscriptions</th>
                  </tr>
                </thead>
                <tbody>
                  {myCodes.map(c => {
                    const used = codeUsageFiltered[(c.id || "").toUpperCase()] || 0;
                    const editing = codeEditingId === c.id;
                    return (
                      <tr key={c.id}>
                        <td style={{minWidth: 220}}>
                          {editing ? (
                            <div className="d-flex gap-2">
                              <input
                                className="form-control form-control-sm"
                                value={codeLabelValue}
                                onChange={(e)=>setCodeLabelValue(e.target.value)}
                                maxLength={60}
                                autoFocus
                              />
                              <button className="btn btn-success btn-sm" onClick={()=>handleSaveCodeLabel(c.id)}>OK</button>
                              <button className="btn btn-outline-secondary btn-sm" onClick={()=>{ setCodeEditingId(null); setCodeLabelValue(""); }}>Annuler</button>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center gap-2">
                              <span className="text-truncate" title={c.label || "—"}>{c.label || "—"}</span>
                              <button className="btn btn-outline-secondary btn-sm" onClick={()=>{ setCodeEditingId(c.id); setCodeLabelValue(c.label || ""); }}>Modifier</button>
                            </div>
                          )}
                        </td>
                        <td><code>{c.id}</code></td>
                        <td>{String(c.active)}</td>
                        <td>{c.discount ?? 50} DH</td>
                        <td>{c.subject || "-"}</td>
                        <td>{c.createdAt?.toDate ? c.createdAt.toDate().toLocaleString() : "-"}</td>
                        <td>{used}</td>
                      </tr>
                    );
                  })}
                  {myCodes.length === 0 && (
                    <tr><td colSpan={7} className="text-muted">Aucun code pour l'instant.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="small text-muted mt-2">
              Le champ <em>Mémo</em> sert uniquement à vous aider à mémoriser à quoi correspond chaque code; il n'a aucun impact fonctionnel sur le code lui‑même.
            </div>
          </>
          );
        })()}

        {/* Vue Contenu pédagogique (2Bac ou 3AC Fr) */}
        {(activeTab === "content" || activeTab === "content3ac") && (
          // Vue Contenu pédagogique - Nouvelle disposition
          <div className="row g-4">
            {/* FICHIERS ET DOSSIERS - COTE GAUCHE */}
            <div className="col-lg-8">
              <div className="card shadow-sm transparent-card">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <h5 className="card-title mb-0">Fichiers et dossiers</h5>
                    <span className="badge text-bg-secondary">{activeTab === "content3ac" ? "3AC Fr" : "2Bac"}</span>
                    {currentFolderId && <button className="btn btn-sm btn-outline-secondary" onClick={goUp}>← Retour</button>}
                  </div>

                  {/* Fil d'Ariane */}
                  <nav aria-label="breadcrumb" className="mb-3">
                    <ol className="breadcrumb mb-0">
                      {crumbs.map((c,i)=>(
                        <li key={c.id ?? "root"}
                            className={"breadcrumb-item " + (i===crumbs.length-1 ? "active" : "")}
                            aria-current={i===crumbs.length-1 ? "page" : undefined}>
                          {i===crumbs.length-1 ? (
                            <span>{c.name}</span>
                          ) : (
                            <button className="btn btn-link p-0 text-decoration-none" onClick={()=>setCurrentFolderId(c.id)}>{c.name}</button>
                          )}
                        </li>
                      ))}
                    </ol>
                  </nav>

                  {/* Contrôles de tri */}
                  <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                    <div className="input-group" style={{maxWidth: 400}}>
                      <span className="input-group-text">Trier par</span>
                      <select 
                        className="form-select" 
                        value={sortMode} 
                        onChange={(e)=>setSortMode(e.target.value)}
                      >
                        <option value="name">Nom</option>
                        <option value="date">Date</option>
                        <option value="size">Taille</option>
                        <option value="custom">Personnalisé</option>
                      </select>
                    </div>
                    <span className="text-muted small">(L'ordre personnalisé sera appliqué côté élèves)</span>
                  </div>

                  {/* Zone drop upload */}
                  <div
                    className={`p-3 mb-3 rounded border ${isOverDrop ? "border-2 border-primary bg-light" : "border-secondary"} transparent-card`}
                    onDragEnter={onFilesDragEnter}
                    onDragOver={onFilesDragOver}
                    onDragLeave={onFilesDragLeave}
                    onDrop={onFilesDrop}
                  >
                    <div className="mb-2 text-muted">Glissez <strong>un ou plusieurs</strong> fichiers ici pour les téléverser dans ce dossier</div>

                    {/* Sélection de la cible de dépôt */}
                    <div className="mb-2 d-flex align-items-center justify-content-between">
                      <div className="btn-group" role="group" aria-label="Cible du dépôt">
                        <button
                          type="button"
                          className={`btn btn-sm ${uploadTarget==='students' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setUploadTarget('students')}
                        >
                          Élèves inscrits
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${uploadTarget==='free' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setUploadTarget('free')}
                        >
                          Contenu gratuit
                        </button>
                      </div>
                      <small className="text-muted ms-2">
                        Dépôt pour {activeTab==='content3ac' ? '3AC Fr' : '2Bac'} — {(profile?.subject || 'Matière')}
                      </small>
                    </div>

                    <div className="input-group">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Choisir des fichiers
                      </button>
                      <input
                        type="text"
                        className="form-control"
                        readOnly
                        value={selectedLabel}
                      />
                      <button
                        className="btn btn-primary"
                        disabled={!filesToUpload.length || uploading}
                        onClick={doUpload}
                      >
                        {uploading ? "Téléversement…" : "Téléverser"}
                      </button>
                      {filesToUpload.length > 0 && (
                        <button className="btn btn-outline-secondary" disabled={uploading} onClick={()=>{ setFilesToUpload([]); if(fileInputRef.current) fileInputRef.current.value=""; }}>Vider</button>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="video/*,application/pdf,image/*"
                      className="d-none"
                      onChange={(e)=>{ setUploadErr(""); setFilesToUpload(Array.from(e.target.files || [])); }}
                    />
                  </div>

                  {pct>0 && (<div className="progress mb-3"><div className="progress-bar" style={{width:pct+"%"}}>{pct}%</div></div>)}
                  {uploadErr && (<div className="alert alert-danger py-2">{uploadErr}</div>)}

                  {/* Créer dossier */}
                  <div className="mb-3">
                    <div className="mb-2">Créer un dossier</div>
                    <div className="input-group mb-2">
                      <input className="form-control" placeholder="Nom du dossier"
                        value={newFolderName} onChange={(e)=>setNewFolderName(e.target.value)} />
                      <button className="btn btn-outline-primary" onClick={createFolder}>Créer</button>
                    </div>
                    <div className="form-check form-switch mb-3">
                      <input className="form-check-input" type="checkbox" id="switchRestricted"
                        checked={newFolderRestricted} onChange={(e)=>setNewFolderRestricted(e.target.checked)} />
                      <label className="form-check-label" htmlFor="switchRestricted">
                        Dossier <span className="text-danger fw-semibold">inaccessible aux élèves</span> (visible mais verrouillé)
                      </label>
                    </div>
                  </div>

                  {/* Affichage en grille des dossiers et fichiers */}
                  <div className="row g-3">
                    {/* Dossiers */}
                    {folders.map((f)=>(
                      <div key={f.id} className="col-md-6 col-lg-4">
                        <div 
                          className={`card h-100 transparent-card ${f.restricted ? 'text-muted' : ''} ${dragOverFolderId===f.id ? 'border-primary' : ''}`}
                          onDragOver={(e)=>onFolderDragOver(e,f.id)}
                          onDrop={(e)=>onFolderDrop(e,f.id)}
                        >
                          <div className="card-body">
                            <div className="d-flex align-items-start justify-content-between gap-2">
                              <button 
                                className={`btn btn-link text-decoration-none p-0 text-start flex-grow-1 ${f.restricted ? 'text-muted' : ''}`}
                                onClick={()=>!f.restricted && setCurrentFolderId(f.id)}
                                disabled={f.restricted}
                              >
                                {f.restricted ? "🔒" : "📁"} {f.name}
                                {f.restricted && (
                                  <span className="badge bg-danger-subtle text-danger ms-2">inaccessible</span>
                                )}
                              </button>
                            </div>
                            
                            {sortMode === "custom" && (
                              <div className="mt-2 d-flex gap-1">
                                <button 
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => moveItem(f.id, 'up')}
                                  title="Monter"
                                >
                                  ↑
                                </button>
                                <button 
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => moveItem(f.id, 'down')}
                                  title="Descendre"
                                >
                                  ↓
                                </button>
                              </div>
                            )}

                            <div className="mt-2 d-flex flex-wrap gap-1">
                              {renamingId===f.id ? (
                                <>
                                  <input className="form-control form-control-sm" 
                                    value={renameValue} onChange={(e)=>setRenameValue(e.target.value)} />
                                  <button className="btn btn-sm btn-success" onClick={()=>renameFolder(f)}>OK</button>
                                  <button className="btn btn-sm btn-outline-secondary" onClick={()=>setRenamingId(null)}>Annuler</button>
                                </>
                              ) : (
                                <>
                                  <button className="btn btn-sm btn-outline-secondary"
                                    onClick={()=>{ setRenamingId(f.id); setRenameValue(f.name); }}>Renommer</button>
                                  <button className="btn btn-sm btn-outline-warning" onClick={()=>toggleFolderRestricted(f)}>
                                    {f.restricted ? "🔓" : "🔒"}
                                  </button>
                                  <button className="btn btn-sm btn-outline-danger" onClick={()=>deleteFolder(f)}>Supprimer</button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Fichiers */}
                    {files.map((f)=>(
                      <div key={f.id} className="col-md-6 col-lg-4">
                        <div className="card h-100 transparent-card" draggable onDragStart={(e)=>onFileDragStart(e,f)}>
                          <div className="card-body">
                            <div className="d-flex align-items-start justify-content-between gap-2">
                              <button 
                                className="btn btn-link text-decoration-none p-0 text-start flex-grow-1"
                                onClick={()=>onOpen(f)}
                              >
                                {f.name?.toLowerCase().endsWith('.pdf') ? '📄' : 
                                 f.name?.toLowerCase().match(/\.(mp4|avi|mov|webm)$/) ? '🎬' : 
                                 f.name?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/) ? '🖼️' : '📎'} {f.name}
                              </button>
                            </div>
                            
                            {sortMode === "custom" && (
                              <div className="mt-2 d-flex gap-1">
                                <button 
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => moveItem(f.id, 'up')}
                                  title="Monter"
                                >
                                  ↑
                                </button>
                                <button 
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => moveItem(f.id, 'down')}
                                  title="Descendre"
                                >
                                  ↓
                                </button>
                              </div>
                            )}

                            <div className="mt-2 d-flex flex-wrap gap-1">
                              {fileRenamingId === f.id ? (
                                <>
                                  <input className="form-control form-control-sm" 
                                    value={fileRenameValue} onChange={(e)=>setFileRenameValue(e.target.value)} />
                                  <button className="btn btn-sm btn-success" onClick={async ()=>{ await updateDoc(doc(db, "contents", f.id), { name: (fileRenameValue||"").trim(), updatedAt: serverTimestamp() }); setFileRenamingId(null); setFileRenameValue(""); }}>OK</button>
                                  <button className="btn btn-sm btn-outline-secondary" onClick={()=>{ setFileRenamingId(null); setFileRenameValue(""); }}>Annuler</button>
                                </>
                              ) : (
                                <>
                                  <button className="btn btn-sm btn-primary" onClick={()=>onOpen(f)}>Lire</button>
                                  <button className="btn btn-sm btn-outline-secondary" onClick={()=>{ setFileRenamingId(f.id); setFileRenameValue(f.name); }}>Renommer</button>
                                  <button className="btn btn-sm btn-outline-danger" onClick={() => deleteFile(f)} >Supprimer</button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {(folders.length === 0 && files.length === 0) && (
                    <div className="text-center text-muted py-4">
                      Aucun dossier ou fichier dans ce dossier
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* APERÇU - COTE DROIT */}
            <div className="col-lg-4">
              <div className="card shadow-sm transparent-card">
                <div className="card-body">
                  <h5 className="card-title mb-3">Aperçu</h5>
                  <div ref={previewRef} className="mt-3 border rounded p-2">
                    <PreviewPane />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .prof-bg{
          position: fixed;
          inset: 0;
          z-index: -1;
          background-image: var(--prof-bg);
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          background-attachment: fixed;      /* effet fluide desktop */
          transform: translateZ(0);
        }
        /* voile léger pour la lisibilité du contenu */
        .prof-bg::after{
          content:"";
          position:absolute; inset:0;
          background: linear-gradient(0deg, rgba(255,255,255,.60), rgba(255,255,255,.30));
        }
        /* sur mobile/tablette on évite le fixed (iOS/Android) */
        @media (max-width: 991.98px){
          .prof-bg{ background-attachment: scroll; }
        }

        /* Cartes transparentes pour Prof.jsx */
        .transparent-card {
          background: rgba(255, 255, 255, 0.7) !important;
          backdrop-filter: blur(10px);
        }
      `}</style>
    </>
  );
}

export default function Prof() {
  return (
    <RequireAuth role="prof">
      <ProfInner />
    </RequireAuth>
  );
}
